import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FcSearch } from 'react-icons/fc';
import { useSidebarSearch } from '../hooks/useSidebarSearch';
import { FlattenedNavItem } from '../utils/flattenNavItems';
import SearchErrorBoundary from './ErrorBoundary/SearchErrorBoundary';

function SearchInput() {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchError, setSearchError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { search, getAllItems } = useSidebarSearch();
  
  // Safe search hook usage with error handling
  interface SearchItem {
    item: FlattenedNavItem;
  }
  
  let searchResults: SearchItem[] = [];
  let results: SearchItem['item'][] = [];
  
  try {
    // Get search results based on query with error handling
    searchResults = query.trim() ? search(query) : getAllItems();
    results = searchResults.map(r => r.item).slice(0, query.trim() ? 50 : 10); // Limit results
    
    // console.log('[SearchInput] Search/getAllItems results:', {
    //   query: query.trim(),
    //   searchResultsCount: searchResults.length,
    //   finalResultsCount: results.length,
    //   searchResults: searchResults,
    //   finalResults: results
    // });
    
    // Clear any previous errors
    if (searchError) {
      setSearchError(null);
    }
  } catch (error) {
    console.error('Search functionality error:', error);
    setSearchError(t('common.search_unavailable'));
    searchResults = [];
    results = [];
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle Cmd+K / Ctrl+K shortcut to focus the search
      // Removed auto-focus on typing feature as it interferes with other inputs on the page
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % results.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + results.length) % results.length);
        break;
      case 'Enter':
        e.preventDefault();
        if (results[selectedIndex]?.urlPath) {
          navigate(results[selectedIndex].urlPath);
          setIsOpen(false);
          setQuery('');
          inputRef.current?.blur();
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(true);
    setSelectedIndex(0);
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleResultClick = (urlPath: string) => {
    navigate(urlPath);
    setIsOpen(false);
    setQuery('');
  };

  const escapeRegex = (value: string) =>
    value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const highlightMatch = (text: string, searchQuery: string) => {
    if (!searchQuery) return text;

    const escapedQuery = escapeRegex(searchQuery);
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) =>
      part.toLowerCase() === searchQuery.toLowerCase() ? (
        <span key={index} className="bg-blue-100 text-blue-900 font-semibold">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <div className="relative flex-1 max-w-lg">
      <div className="relative">
        <FcSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={t('search.placeholder')}
          className="w-full pl-10 pr-20 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 hidden sm:block">
          ⌘K
        </span>
      </div>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-y-auto z-50"
        >
          {searchError ? (
            <div className="px-4 py-8 text-center text-red-500">
              <p className="text-sm">{searchError}</p>
              <p className="text-xs mt-1">Please try again later</p>
            </div>
          ) : results.length > 0 ? (
            <ul className="py-2">
              {results.map((item, index) => (
                <li key={item.code}>
                  <button
                    onClick={() => item.urlPath && handleResultClick(item.urlPath)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                      index === selectedIndex ? 'bg-gray-50' : ''
                    } ${!item.urlPath ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={!item.urlPath}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {highlightMatch(item.name, query)}
                        </div>
                        {item.fullPath && (
                          <div className="text-xs text-gray-500 mt-1">
                            {item.fullPath.split(' > ').map((part: string, pathIndex: number) => (
                              <span key={`${item.code}_path_${pathIndex}`}>
                                {pathIndex > 0 && <span className="mx-1">›</span>}
                                {part}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : query.trim() ? (
            <div className="px-4 py-8 text-center text-gray-500">
              <p className="text-sm">{t('search.noResults', 'Nenhum resultado encontrado')}</p>
              <p className="text-xs mt-1">{t('search.tryAgain', 'Tente buscar com outros termos')}</p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

// Wrap SearchInput with specialized error boundary
const SearchInputWithErrorBoundary: React.FC = () => (
  <SearchErrorBoundary showFallback={true}>
    <SearchInput />
  </SearchErrorBoundary>
);

export default SearchInputWithErrorBoundary;